import {
  Injectable,
  ElementRef,
  Injector,
  Renderer2,
  RendererFactory2,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import * as _ from 'lodash';
import concat from 'lodash/concat';
import filter from 'lodash/filter';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import some from 'lodash/some';

import { ClipboardService, TranslationService } from '@alfresco/adf-core';

import { CKEditorComponent } from '../../../../app/components/ckeditor';
import { WKDeleteDecoratorsDialogComponent } from '../../../../app/components/shared/dialogs/message-dialog/wk-delete-decorators-dialog/wk-delete-decorators-dialog.component';
import {
  CustomCommentThread,
  SaveCommentThreadModel,
} from '../../../../app/model';
import {
  PluginDecorationDataService,
  ReferenceTypes,
} from '../../../../app/services';
import { WkEditorService } from '../../../services/wk-editor.service';
import { WkInsertDocumentLinkService } from '../../../services/wk-link-document.service';
import { WINDOW_TOKEN } from '../../../components/shared/window.token';

import { CommonConstants, LocalStorage } from '../../../shared';
import { ContextMenuEditorService } from '../../../services';
import { deduplicateTrackChangesInCitation } from '../../../utils';
import { AppStore } from '../../../store/states/app.state';
import { SnackbarErrorAction } from '../../../store/actions/app.custom-actions';

import {
  setCitationUpdated,
  setRHPTrackChangesData,
  setSelectedChevronData,
  setSelectedReferenceId,
  setCopiedNumberedCitationId,
  getRHPTrackChange,
} from '../state';
import {
  ContextMenuCoords,
  ISuggestion,
  NumberedCitation,
  ActionTypes,
  DIRECTION,
} from '../models';
import {
  DecoratorsConstant as DC,
  DecoratorsTrackChangeConstant,
} from '../const';

const {
  CK_PLUGINS: {
    INSERT_LINK,
    CMD_TRACK_CHANGES,
    ACCEPT_SUGGESTION,
    ACCEPTALL_SUGGESTION,
    DISCARD_SUGGESTION,
    DISCARDALL_SUGGESTION,
  },
  trackchangesMarkup: { SUGGESTION },
  CONTENT_EDITABLE,
} = CommonConstants;

@Injectable({
  providedIn: 'root',
})
export class WkDecoratorActionService {
  // #region Properties (7)

  private readonly globalWindow: Window;
  private readonly insertDocumentLinkService: WkInsertDocumentLinkService;
  private readonly onDestroy$: Subject<boolean> = new Subject<boolean>();
  private readonly renderer: Renderer2;
  private readonly dialogRef: MatDialog;
  private _contextMenuCoords: ContextMenuCoords;
  public setEditorClicked = new BehaviorSubject<string>('');
  private readonly parser: DOMParser = new DOMParser();
  private readonly editorService: WkEditorService;
  private readonly pluginDecorationService: PluginDecorationDataService;
  private readonly trackchangeStorageKey =
    DecoratorsTrackChangeConstant.TRACK_CHANGES_AND_COMMENTS_DATA;
  private readonly clipboardService: ClipboardService;
  private readonly translationService: TranslationService;
  private readonly contextmenuService: ContextMenuEditorService;
  private scrolledId: string;
  private idToggle: number;
  private rhpTrackChangeComment = { suggestions: [], commentThreads: [] };
  // #endregion Properties (7)

  // #region Constructors (1)

  constructor(
    private readonly store: Store<AppStore>,
    private readonly rendererFactory: RendererFactory2,
    private readonly injector: Injector
  ) {
    this.rendererFactory =
      this.injector.get<RendererFactory2>(RendererFactory2);
    this.clipboardService =
      this.injector.get<ClipboardService>(ClipboardService);
    this.renderer = this.rendererFactory.createRenderer(null, null);
    this.globalWindow = this.injector.get<Window>(WINDOW_TOKEN);
    this.insertDocumentLinkService =
      this.injector.get<WkInsertDocumentLinkService>(
        WkInsertDocumentLinkService
      );

    this.dialogRef = this.injector.get<MatDialog>(MatDialog);
    this.editorService = this.injector.get<WkEditorService>(WkEditorService);
    this.pluginDecorationService =
      this.injector.get<PluginDecorationDataService>(
        PluginDecorationDataService
      );
    this.translationService =
      this.injector.get<TranslationService>(TranslationService);
    this.contextmenuService = this.injector.get<ContextMenuEditorService>(
      ContextMenuEditorService
    );
    this.store
      .select(getRHPTrackChange)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((data) => {
        this.rhpTrackChangeComment = data || {
          suggestions: [],
          commentThreads: [],
        };
      });
  }

  // #endregion Constructors (1)

  // #region Public Methods (4)

  public routeToLink(elementRef: ElementRef, event: any): void {
    const linkAddress = event.linkAttributes
      ? event.linkAttributes.linkAddress
      : event.target.getAttribute(CommonConstants.resourceAttrName) ||
        event.target?.parentElement?.getAttribute(
          CommonConstants.resourceAttrName
        );
    const linkElement = elementRef.nativeElement.querySelector(
      `[resource='${linkAddress}']`
    );
    const typeOf = linkElement?.getAttribute('typeof');
    if (typeOf === CommonConstants.pciCoreWebResource) {
      this.globalWindow.open(
        `${linkElement.getAttribute('resource')}`,
        '_blank'
      );
    } else {
      linkElement?.getAttribute('redirectLink')
        ? this.globalWindow.open(
            `${linkElement.getAttribute('redirectLink')}`,
            '_blank'
          )
        : this.redirectToLinkAddress(linkAddress, linkElement);
    }
  }

  public deleteNumberedReference(
    numberedRef,
    citationCount = null
  ): MatDialogRef<WKDeleteDecoratorsDialogComponent> {
    return this.dialogRef.open(WKDeleteDecoratorsDialogComponent, {
      height: 'auto',
      width: '419px',
      panelClass: 'wk-delete-citation-dialog',
      autoFocus: false,
      data: {
        citationEditType: ReferenceTypes.NumberedRef,
        totalCitedCount: numberedRef.referencedBy.length,
        citationCount,
      },
    });
  }

  public deleteUnNumberedReference(): MatDialogRef<WKDeleteDecoratorsDialogComponent> {
    return this.dialogRef.open(WKDeleteDecoratorsDialogComponent, {
      height: 'auto',
      width: '419px',
      panelClass: 'wk-delete-citation-dialog',
      autoFocus: false,
      data: {
        citationEditType: ReferenceTypes.UnnumberedRef,
      },
    });
  }

  public get contextMenuCoords() {
    return this._contextMenuCoords;
  }

  public set contextMenuCoords(data: ContextMenuCoords) {
    this._contextMenuCoords = {
      left: data.left,
      top: data.top,
      clientX: data.clientX,
      marginLeft: data.marginLeft,
      marginRight: data.marginRight,
      offsetHeight: data.offsetHeight,
    };
  }

  // #endregion Public Methods (4)

  // #region Private Methods (1)

  private redirectToLinkAddress(linkAddress, linkElement): void {
    this.insertDocumentLinkService
      .getNodeOfLinkedDoc(linkAddress)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (data) => {
          const docId = data.list.entries[0].entry.id;
          this.globalWindow.open(`#/dashboard/preview/${docId}`, '_blank');
          this.renderer.setAttribute(
            linkElement,
            'redirectLink',
            `#/dashboard/preview/${docId}`
          );
          this.onDestroy$.next(false);
        },
        error: (error) => {
          this.store.dispatch(new SnackbarErrorAction(error.message));
        },
      });
  }

  // #endregion Private Methods (1)
  /**
   * Get selected content as string from ckeditor
   *
   * @public
   * @param {CKEditorComponent} ckeditor
   * @returns {string}
   */
  public getSelectedHTMLString(ckeditor: CKEditorComponent): string {
    return ckeditor.editorInstance.data.stringify(
      ckeditor.editorInstance.model.getSelectedContent(
        ckeditor.editorInstance.model.document.selection
      )
    );
  }

  public onEditCitations({
    nodeList,
    references,
    action,
    ckeditor,
    selectedTab,
    trackChangesAndCommentsData,
  }): void {
    const _references = filter(
      map(nodeList, (item: HTMLElement) => {
        const citationId = item.getAttribute(DC.DATA_CITATION_ID);
        const index = findIndex(references, { citationId });

        // Guard clause to handle undefined citation
        if (index === -1) {
          console.warn(`No citation found for id: ${citationId}`);
          return null; // Skip this citation
        }

        const citation = references[index];

        // Guard against undefined citation or citationString
        if (!citation || !citation.citationString) {
          console.warn(
            `Citation or citationString is null for id: ${citationId}`
          );
          return null; // Skip this citation
        }

        const bodyContent = this.parser.parseFromString(
          citation.citationString,
          'text/html'
        ).body;

        const prefLabelElement = bodyContent.querySelector(
          `[property="${DC.CITATION_PREF_LABEL}"]`
        );
        const citationTextElement = item.querySelector(`.${DC.CITATION_TEXT}`);

        // Ensure elements exist before setting innerHTML
        if (prefLabelElement && citationTextElement) {
          prefLabelElement.innerHTML = citationTextElement.innerHTML;
        } else {
          if (!prefLabelElement) {
            console.warn(
              `Pref label element not found in citation for id: ${citationId}`
            );
          }
          if (!citationTextElement) {
            console.warn(
              `Citation text element not found for id: ${citationId}`
            );
          }
        }

        // Convert NodeList to an array and use Lodash forEach
        const links = Array.from(
          bodyContent.querySelectorAll('span[rel="pcicore:linksTo"]')
        );
        forEach(links, (link) => {
          if (link instanceof HTMLElement && link.hasAttribute('about')) {
            link.removeAttribute('about');
          }
        });
        // Clean duplicate comma-separated values in all 6 suggestion-related attributes
        deduplicateTrackChangesInCitation(bodyContent);
        // Normalize CKEditor serialization artifacts in citation HTML markup.
        // Removes editor noise (fillable breaks, whitespace, special chars), attribute artifacts, and normalizes
        // spacing around suggestion markers (text content spaces are preserved; only HTML structure whitespace is normalized).
        citation.citationString = bodyContent.innerHTML
          .replace(/<br data-cke-filler="true"><\/span>/g, '</span>')
          .replace(/<br>&nbsp;<\/span>/g, '</span>')
          .replace(/&nbsp;<\/span>/g, '</span>')
          .replace(/' ⁠⁠⁠⁠⁠⁠⁠'<\/span>/g, '</span>')
          .replace(/'⁠⁠⁠⁠⁠⁠⁠'<\/span>/g, '</span>')
          .replace(
            /contenteditable='true'|contenteditable="true"|contenteditable=\"true\"/gi,
            ''
          )
          .replace(/ (<suggestion-end\s)/g, '&nbsp;$1');
        return citation;
      }),
      (citation) => citation !== null
    ); // Filter out any null citations

    this.getSuggestionsAndCommentData(
      ckeditor,
      selectedTab,
      trackChangesAndCommentsData
    );

    this.store.dispatch(action({ references: _references }));
    this.store.dispatch(setCitationUpdated({ isCitationUpdated: true }));
  }

  public getSuggestionsAndCommentData(
    ckeditor,
    selectedTab,
    trackChangesAndCommentsData?
  ): void {
    const editor = ckeditor?.editorInstance;
    const trackChangesEditingPlugin = editor?.plugins?.get?.(
      'TrackChangesEditing'
    );
    const trackChangesPlugin = editor?.plugins?.get?.('TrackChanges');
    const rawSuggestions =
      typeof trackChangesEditingPlugin?.getSuggestions === 'function'
        ? trackChangesEditingPlugin.getSuggestions({
            skipNotAttached: true,
            toJSON: true,
          })
        : typeof trackChangesPlugin?.getSuggestions === 'function'
        ? trackChangesPlugin.getSuggestions({
            skipNotAttached: true,
            toJSON: true,
          })
        : [];
    const sugg =
      map(rawSuggestions, (suggestion) => ({
        ...suggestion,
        isRHPSuggestion: true,
        fromTab: selectedTab,
      })) ?? [];
    const inactiveTabSuggestions = filter(
      this.rhpTrackChangeComment.suggestions,
      (suggestion) => suggestion.fromTab !== selectedTab
    );
    const suggestions = _.uniqBy([...sugg, ...inactiveTabSuggestions], 'id');

    const commentThreadsPlugin = editor?.plugins.get('CommentsRepository');
    const commentThreads =
      commentThreadsPlugin
        ?.getCommentThreads({
          skipEmpty: true,
          skipNotAttached: true,
          toJSON: true,
        })

        .map((commentThread: CustomCommentThread) => {
          commentThread.comments = this.editorService.getCommentsPayload(
            commentThread,
            suggestions,
            { addedReleaseNoteIds: [], addedUMCReleaseNoteIds: [] }
          );
          return {
            threadId: commentThread.threadId,
            comments: commentThread.comments,
            isRHPComment: true,
            fromTab: selectedTab,
          };
        }) || [];

    if (trackChangesAndCommentsData) {
      trackChangesAndCommentsData = {
        commentThreads: concat(
          filter(
            trackChangesAndCommentsData.commentThreads,
            (commentThread) => commentThread.fromTab !== selectedTab
          ),
          commentThreads
        ),
        suggestions: concat(
          filter(
            trackChangesAndCommentsData.suggestions,
            (suggestion) => suggestion.fromTab !== selectedTab
          ),
          suggestions
        ),
      };
    } else {
      trackChangesAndCommentsData = {
        commentThreads,
        suggestions,
      };
    }

    try {
      const {
        suggestions: storedSuggestions = [],
        commentThreads: storedCommentThreads = [],
      } = JSON.parse(localStorage.getItem(this.trackchangeStorageKey) || '{}');

      const suggestionsToPreserve = storedSuggestions.filter(
        ({ isRHPSuggestion, fromTab }) =>
          !isRHPSuggestion || fromTab !== selectedTab
      );

      const commentThreadsToPreserve = storedCommentThreads.filter(
        ({ isRHPComment, fromTab }) => !isRHPComment || fromTab !== selectedTab
      );

      localStorage.setItem(
        this.trackchangeStorageKey,
        JSON.stringify({
          suggestions: this.getUniqueTrackChangesAndCommentsData(
            trackChangesAndCommentsData.suggestions,
            suggestionsToPreserve,
            'id'
          ),
          commentThreads: this.getUniqueTrackChangesAndCommentsData(
            trackChangesAndCommentsData.commentThreads,
            commentThreadsToPreserve,
            'threadId'
          ),
        })
      );
    } catch (error) {
      console.warn(`Error: ${error.message}`);
      throw error;
    }

    this.store.dispatch(
      setRHPTrackChangesData({
        trackChangeRHPData: trackChangesAndCommentsData,
      })
    );
  }

  public checkEmptyCitation(ckContainer): void {
    if (!ckContainer) {
      return;
    }
    const referenceBlocks: NodeList =
      ckContainer.nativeElement.querySelectorAll(
        `.${DC.NUMBERED_CITATION_CLASS}, .${DC.UNNUMBERED_CITATION_CLASS}`
      );
    forEach(Array.from(referenceBlocks), (item: HTMLElement) => {
      if (
        item.lastElementChild.firstElementChild.textContent.trim() === '' &&
        !item.lastElementChild.firstElementChild.classList.contains(
          DC.CITATION_SELECTED_CLASS
        )
      ) {
        this.renderer.addClass(
          item.lastElementChild.firstElementChild,
          DC.CITATION_ERROR_CLASS
        );
      } else {
        this.renderer.removeClass(
          item.lastElementChild.firstElementChild,
          DC.CITATION_ERROR_CLASS
        );
      }
    });
  }

  public hideFindDialog(target: ElementRef, ckeditor): void {
    try {
      const findAndReplaceDialog =
        ckeditor.editorInstance.plugins.get('Dialog');
      findAndReplaceDialog.id === 'findAndReplace' &&
        !findAndReplaceDialog?.view?.element?.contains(target) &&
        findAndReplaceDialog.hide();
    } catch (err) {
      console.warn(err);
    }
  }

  public removeValidation(ckContainer): void {
    const numunumBlocks: NodeList = ckContainer.nativeElement.querySelectorAll(
      `.${DC.CITATION_TEXT}`
    );
    forEach(Array.from(numunumBlocks), (item: HTMLElement) => {
      const parent = item.closest(
        `.${DC.NUMBERED_CITATION_CLASS}, .${DC.UNNUMBERED_CITATION_CLASS}`
      );
      this.renderer.removeClass(parent, DC.BG_CLASS);
      if (!item.classList.contains(DC.CITATION_ERROR_CLASS)) {
        this.renderer.removeClass(item, DC.CITATION_SELECTED_CLASS);
      }
    });
  }

  public dispatchHighlightId(id, navDirection, referencedBy): void {
    this.store.dispatch(
      setSelectedChevronData({
        chevronData: {
          id,
          navDirection,
          referencedBy,
        },
      })
    );
  }

  public setPluginIsEnable(value, ckeditor): void {
    if (ckeditor?.editorInstance?.commands) {
      forEach(
        [CommonConstants.LINK_PLUGIN_CMD, CommonConstants.INSERT_TEXT_CMD],
        (item: string) => {
          ckeditor.editorInstance.commands.get(item).isEnabled = value;
        }
      );
    }
  }

  public getSelectedCitationRefBy(event, references): NumberedCitation | null {
    const target = event.target as HTMLElement;
    const parent = target.closest(`[${DC.DATA_CITATION_ID}]`);
    if (!parent) {
      return null;
    }
    const citationId = parent.getAttribute(DC.DATA_CITATION_ID);
    return find(references, { citationId });
  }

  public markNewReferenceAsInsertion(editor, selectedReferenceIdToMark): void {
    const createdRange = editor.model.createRangeIn(
      editor.model.document.getRoot()
    );
    const trackChangesEditingPlugin = editor.plugins.get('TrackChangesEditing');
    for (const item of createdRange.getItems()) {
      const referenceFound =
        item.getAttribute('data-citation-id') === selectedReferenceIdToMark;
      try {
        if (
          referenceFound &&
          item.getChild(1)?.childCount === 1 &&
          (item.getChild(1)?.getChild(0)?.name === 'paragraph' || 'p')
        ) {
          _.forEach(item.getChild(1).getChild(0)._children?._nodes, (ele) => {
            const rng = editor.model.createRangeOn(ele);
            trackChangesEditingPlugin.markInsertion(rng);
          });
        }
      } catch (err) {
        throw new Error(err);
      }
    }
  }

  public async getTrackChangesConfigData(
    editor: CKEditorComponent,
    enableTrackChangesCommand: boolean
  ): Promise<void> {
    if (!editor?.editorInstance) {
      return;
    }
    const tenantSpecifiedData: any = await firstValueFrom(
      this.pluginDecorationService.getEnablePluginData()
    );
    if (
      some(
        tenantSpecifiedData,
        (item) =>
          item.name === CommonConstants.CK_PLUGINS.RHP_TRACK_CHANGES &&
          item.value
      )
    ) {
      editor.editorInstance.commands.get(
        CommonConstants.CK_PLUGINS.CMD_TRACK_CHANGES
      ).isEnabled = true;
      editor.editorInstance.commands.get(
        CommonConstants.CK_PLUGINS.CMD_TRACK_CHANGES
      ).value = enableTrackChangesCommand;
    }
  }

  /**
   * Clears the selection in the RHP on click of disabled element.
   * @param ckeditor - The ckeditor instance.
   */
  public clearSelection(ckeditor: any): void {
    ckeditor.editorInstance.model.change((writer) => {
      writer.setSelection(null);
    });
  }

  /**
   * Disables the "insertlink" command in the RHP toolbar.
   * @param ckeditor - The ckeditor instance.
   */
  public disableCustomCommand(ckeditor: any): void {
    const commandsToDisable = [
      INSERT_LINK,
      CMD_TRACK_CHANGES,
      ACCEPT_SUGGESTION,
      ACCEPTALL_SUGGESTION,
      DISCARD_SUGGESTION,
      DISCARDALL_SUGGESTION,
    ];
    forEach(commandsToDisable, (command) => {
      ckeditor.editorInstance.commands.get(command).isEnabled = false;
    });
  }

  /**
   * Disables or enables toolbar items in the editor based on the provided list.
   * @param ckeditor - The ckeditor instance.
   * @param itemsToDisable - The list of toolbar items to disable or enable.
   * @param isEnabled - A boolean indicating whether to disable (false) or enable (true) the items.
   */
  public disableToolbarItems(ckeditor: any, isEnabled: boolean): void {
    forEach(
      ckeditor?.editorInstance?.ui?.view?.toolbar?.items?._items,
      (item) => {
        if (item?.label) {
          item.isEnabled = isEnabled;
        }
      }
    );
  }

  public updateSourceEditingLabel(ckeditor: any, sourceLabel: string): void {
    forEach(
      ckeditor?.editorInstance?.ui?.view?.toolbar?.items?._items,
      (item) => {
        if (this.isSourceEditingToolbarItem(item)) {
          item.label = sourceLabel;
        }
      }
    );

    this.updateSourceEditingDialogTitle(sourceLabel);
  }

  public bindSourceEditingDialogTitle(
    ckeditor: any,
    getSourceLabel: () => string
  ): void {
    const dialogPlugin = ckeditor?.editorInstance?.plugins?.get?.('Dialog');

    if (!dialogPlugin || dialogPlugin.rhpSourceLabelListenerBound) {
      return;
    }

    dialogPlugin.rhpSourceLabelListenerBound = true;
    dialogPlugin.on(
      'change:isOpen',
      (_eventInfo, _propertyName, isOpen: boolean) => {
        if (isOpen) {
          setTimeout(
            () => this.updateSourceEditingDialogTitle(getSourceLabel()),
            0
          );
        }
      }
    );
  }

  private updateSourceEditingDialogTitle(sourceLabel: string): void {
    const dialogLabel = this.globalWindow?.document?.querySelector(
      '.ck-dialog-overlay .ck.ck-form__header .ck-form__header__label'
    );

    if (dialogLabel) {
      dialogLabel.textContent = sourceLabel;
    }
  }

  private isSourceEditingToolbarItem(item: any): boolean {
    const label = item?.label;

    return (
      typeof label === 'string' &&
      (label === CommonConstants.CK_PLUGINS.EDIT_SOURCE_LABEL ||
        label.endsWith(` ${CommonConstants.CK_PLUGINS.EDIT_SOURCE_LABEL}`))
    );
  }

  /**
   * This method returns true if the element has the 'content-editable' attribute set to 'true' or if it has the 'suggestion' attribute.
   * @param target - The HTML element to check.
   * @returns A boolean value indicating whether the element is editable.
   */
  private isElementEditable(target: HTMLElement): boolean {
    return (
      (target?.hasAttribute?.(CONTENT_EDITABLE) &&
        target?.getAttribute?.(CONTENT_EDITABLE) === 'true') ||
      target?.hasAttribute?.(SUGGESTION)
    );
  }

  /**
   * This method hides the balloon popup when a click event occurs on a RHP disabled element.
   * @param {Event} event - The event object that is fired when the user clicks on the disabled element.
   */
  public hideBalloonOnClickDisabled(event: any, ckeditor: any): void {
    const target = event.target as HTMLElement;
    const isDisabledElement = target?.closest?.(DC.DISABLE_NUM_BLOCK);
    const isEditableElement = this.isElementEditable(target);

    if (isDisabledElement) {
      this.handleDisabledElement(event, ckeditor);
    } else if (isEditableElement) {
      this.disableToolbarItems(ckeditor, true);
    }
  }

  public navigateToCitation(event, isEditable, ckContainer, ckeditor): void {
    this.hideBalloonOnClickDisabled(event, ckeditor);
    const target = event.target as HTMLElement;
    const parent = target.closest(`[${DC.DATA_CITATION_ID}]`);
    const id = parent?.getAttribute(DC.DATA_CITATION_ID);
    if (parent?.classList.contains(DC.NUMBERED_CITATION_CLASS) && isEditable) {
      const isSelectable =
        (target.className.includes(DC.BG_CLASS) ||
          (target.className.includes('num-unnum-display') &&
            target.parentElement.className.includes(DC.BG_CLASS))) &&
        !['delete', 'file'].some((cls) => target.className.includes(cls)) &&
        target.title !== ActionTypes.PREVIOUS_NUMBERED &&
        target.title !== ActionTypes.NEXT_NUMBERED;
      isSelectable && this.store.dispatch(setSelectedReferenceId({ id }));
    } else if (!isEditable) {
      const {
        pciCoreWebResource,
        pciCoreBibliographicResource,
        pciCoreMediaResource,
        typeOfAttribute,
      } = CommonConstants;
      const typeOf =
        target.getAttribute(typeOfAttribute) ||
        target?.parentElement?.getAttribute(typeOfAttribute);
      const isCoreResource = [
        pciCoreWebResource,
        pciCoreBibliographicResource,
        pciCoreMediaResource,
      ].includes(typeOf);
      if (isCoreResource) {
        this.routeToLink(ckContainer, event);
      } else if (
        parent?.classList.contains(DC.NUMBERED_CITATION_CLASS) &&
        target.title !== ActionTypes.PREVIOUS_NUMBERED &&
        target.title !== ActionTypes.NEXT_NUMBERED
      ) {
        this.store.dispatch(setSelectedReferenceId({ id }));
      }
    }
  }

  /**
   * Handles the event when a disabled element is clicked.
   * This method clears the selection in the CKEditor, prevents the default event behavior,
   * stops the event propagation, disables the insert link command, and disables the toolbar items.
   * @param event - The event object.
   * @param ckeditor - The CKEditor instance.
   */
  private handleDisabledElement(event: any, ckeditor: any): void {
    this.clearSelection(ckeditor);
    event.preventDefault();
    event.stopImmediatePropagation();
    this.disableCustomCommand(ckeditor);
    this.disableToolbarItems(ckeditor, false);
  }
  private getUniqueTrackChangesAndCommentsData<
    T extends ISuggestion | SaveCommentThreadModel
  >(
    trackChangesAndCommentsData: T[],
    currentTrackChangesAndCommentsData: T[],
    key: string
  ): T[] {
    return _.uniqBy(
      _.concat(trackChangesAndCommentsData, currentTrackChangesAndCommentsData),
      key
    );
  }
  // #endregion Private Methods (1)
  public copyNumberReference(element: Element): void {
    try {
      const id = element.getAttribute(DC.DATA_CITATION_ID);
      this.store.dispatch(
        setCopiedNumberedCitationId({ copiedNumberedCitationId: id })
      );
    } catch {
      console.warn('Error');
    }
  }

  public copyUnnumberedReference(element: Element): void {
    let content = element.querySelector(`.${DC.CITATION_TEXT}`).innerHTML;
    const message = this.translationService.instant(
      'APP.CITATIONS.COPY_UNNUMBERED_NUMBERED_REFS_SUCCESS_MESSAGE'
    );
    // Define the regex pattern to match all <span> elements with the class "ck-suggestion-marker"
    const regex =
      /<span\s+class="ck-suggestion-marker[^"]*"[^>]*>(.*?)<\/span>/g;
    content = content.replace(regex, '$1');
    this.clipboardService.copyContentToClipboard(content, message);
  }

  public removeSuggestionsFromReference(
    ref,
    trackChangesAndCommentsData
  ): void {
    const refContent = new DOMParser().parseFromString(
      ref.citationString,
      'text/html'
    ).body;
    const suggestions = Array.from(
      refContent.querySelectorAll(`suggestion-start`)
    ).map((suggestion) => /:(.+)?:/g.exec(suggestion.getAttribute('name'))[1]);
    trackChangesAndCommentsData.suggestions =
      trackChangesAndCommentsData.suggestions.filter(
        (suggestionData) => !suggestions.includes(suggestionData.id)
      );
    trackChangesAndCommentsData.commentThreads =
      trackChangesAndCommentsData.commentThreads.filter(
        (commentThread) => !suggestions.includes(commentThread.threadId)
      );
  }

  public handleCopy(event): void {
    event.stopPropagation();
    const target = event.target as HTMLElement;
    const parent = target.closest(`[${DC.DATA_CITATION_ID}]`);
    parent.classList.contains(DC.UNNUMBERED_CITATION_CLASS)
      ? this.copyUnnumberedReference(parent)
      : this.copyNumberReference(parent);
  }

  public toggleChevron(navDirection, event, references): void {
    try {
      event.stopPropagation();
      const { referencedBy } = this.getSelectedCitationRefBy(event, references);
      this.scrolledId = localStorage.getItem(
        LocalStorage.CURRENT_LHP_SCROLLED_ID
      );
      if (referencedBy.includes(this.scrolledId)) {
        this.idToggle = referencedBy.indexOf(this.scrolledId);
        if (navDirection === DIRECTION.RIGHT) {
          this.idToggle += 1;
        } else {
          this.idToggle -= 1;
        }
        const citationId = referencedBy[this.idToggle]
          ? referencedBy[this.idToggle]
          : navDirection === DIRECTION.RIGHT
          ? referencedBy[0]
          : referencedBy[referencedBy.length - 1];
        this.dispatchHighlightId(citationId, navDirection, referencedBy);
      } else {
        const citationId =
          navDirection === DIRECTION.RIGHT
            ? referencedBy[0]
            : referencedBy[referencedBy.length - 1];
        this.dispatchHighlightId(citationId, navDirection, referencedBy);
      }
    } catch (ex) {
      console.log(`Error: ${ex.message}`);
    }
  }

  public validateCitationBlock(
    ckContainer,
    citationId: string,
    renderer: Renderer2
  ): void {
    if (!ckContainer || !ckContainer.nativeElement) {
      return;
    }
    const block = ckContainer.nativeElement.querySelector(
      `[data-citation-id='${citationId}']`
    );
    if (!block) {
      return;
    }

    const ref = block.lastElementChild.firstElementChild;
    const text = ref.textContent.trim();
    const isEmpty = _.includes(
      [
        CommonConstants.EMPTY_SPACE.NO_BREAK,
        CommonConstants.EMPTY_SPACE.NO_BREAK_SPACE,
        '',
      ],
      text
    );

    if (isEmpty && ref.classList.contains(DC.CITATION_SELECTED_CLASS)) {
      renderer.addClass(ref, DC.CITATION_ERROR_CLASS);
      renderer.removeClass(ref, DC.CITATION_SELECTED_CLASS);
    } else if (!isEmpty && ref.classList.contains(DC.CITATION_ERROR_CLASS)) {
      renderer.addClass(ref, DC.CITATION_SELECTED_CLASS);
      renderer.removeClass(ref, DC.CITATION_ERROR_CLASS);
    }
  }

  public applyCitationSelectionClasses(
    ckContainer,
    citationId: string,
    targetTextContent: string,
    renderer: Renderer2
  ): void {
    const referenceBlocks: NodeList =
      ckContainer.nativeElement.querySelectorAll(
        `.${DC.NUMBERED_CITATION_CLASS}, .${DC.UNNUMBERED_CITATION_CLASS}`
      );
    forEach(Array.from(referenceBlocks), (item: HTMLElement) => {
      const citationElement = item.lastElementChild?.firstElementChild;
      if (item.getAttribute(DC.DATA_CITATION_ID) === citationId) {
        renderer.addClass(item, DC.BG_CLASS);
        if (targetTextContent.trim() === '') {
          renderer.addClass(citationElement, DC.CITATION_ERROR_CLASS);
          renderer.removeClass(citationElement, DC.CITATION_SELECTED_CLASS);
        } else {
          citationElement?.getAttribute?.(CONTENT_EDITABLE) !== 'false' &&
            renderer.addClass(citationElement, DC.CITATION_SELECTED_CLASS);
          renderer.removeClass(citationElement, DC.CITATION_ERROR_CLASS);
        }
      } else {
        renderer.removeClass(item, DC.BG_CLASS);
        renderer.removeClass(citationElement, DC.CITATION_SELECTED_CLASS);
      }
    });
  }

  /**
   * Wires context menu subscriptions: right-click event dispatcher, action handler and scroll restore.
   * @param component - The component instance providing destroy$, rightClickEventDetails, editLink, ckeditor and ckContainer.
   */
  public initContextMenuSubscriptions(
    component: {
      destroy$: Subject<any>;
      rightClickEventDetails: string;
      elementRef: ElementRef;
      ckContainer: any;
      ckeditor: any;
      editLink: (data: any) => void;
    },
    toggleScroll: (enable: boolean) => void
  ): void {
    this.setEditorClicked
      .pipe(takeUntil(component.destroy$))
      .subscribe((data) => {
        component.rightClickEventDetails = data;
      });

    this.contextmenuService
      .getEventDetails()
      .pipe(takeUntil(component.destroy$))
      .subscribe((data) => {
        if (
          data &&
          component.rightClickEventDetails ===
            CommonConstants.editor.RIGHT_HAND_EDITOR &&
          JSON.parse(
            localStorage.getItem(LocalStorage.RHP_CITATION_REF_CLICKED) ??
              'false'
          )
        ) {
          const { goToLink, editLink, deleteLink } =
            CommonConstants.rightClickOptions;
          switch (data.eventName) {
            case goToLink:
              this.routeToLink(
                component.elementRef,
                (component as any)._setClickDetails
              );
              break;
            case editLink:
              component.editLink(data);
              break;
            case deleteLink:
              component.ckeditor.editorInstance.execute('deletewebemaillink', {
                linkText: data.actionDetails.linkText,
                linkAddress: data.actionDetails.linkAddress,
              });
              break;
          }
        }
      });

    this.contextmenuService.contextMenuClose
      .pipe(takeUntil(component.destroy$))
      .subscribe((data) => {
        data && toggleScroll(true);
      });
  }

  public onMouseEventInCK5Editor(event, ckEditorAndElementRef): void {
    const { ckeditor, ckEditorEditableElement, elementRef } =
      ckEditorAndElementRef;
    this.hideBalloonOnClickDisabled(event, ckeditor);
    this.contextMenuCoords = {
      top: event.pageY - 160,
      left: event.offsetX,
      clientX: event.clientX,
      marginLeft: getComputedStyle(ckEditorEditableElement).marginLeft,
      marginRight: getComputedStyle(ckEditorEditableElement).marginRight,
      offsetHeight:
        event.pageY -
        elementRef.nativeElement.querySelector(
          CommonConstants.ckEditorMainClassName
        ).offsetHeight,
    };
  }
}
